/**
 * Account signup — creates a Shopify Customer Account.
 *
 * After signup we redirect to /account/login so the user signs in with
 * the password they just set. We deliberately don't auto-log-in because
 * Shopify may require email verification (configurable on the storefront).
 */
import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Mail, Lock, User, ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { track } from "../lib/analytics";

const MEMBERSHIP_URL = process.env.REACT_APP_MEMBERSHIP_URL || "/membership";

function Frame({ children }) {
    return (
        <div className="min-h-screen bg-[#0B1220] text-[#CBD5E1] flex flex-col">
            <div className="h-px bg-gradient-to-r from-transparent via-[#56CCF2]/70 to-transparent" />
            <main className="flex-1 flex items-start justify-center px-6 pt-20 pb-16">
                <div className="w-full max-w-md">{children}</div>
            </main>
            <footer className="text-center pb-10 px-6 text-[10px] uppercase tracking-[0.22em] text-[#64748B]">
                <Link to="/" className="hover:text-[#56CCF2] inline-flex items-center gap-1.5" data-testid="account-signup-back-home">
                    <ArrowLeft className="w-3 h-3" />
                    Back to the library
                </Link>
            </footer>
        </div>
    );
}

export default function AccountSignupPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    // After signup we send the user through the welcome quiz on first login,
    // unless the URL already requested a specific destination.
    const explicitNext = params.get("next");
    const nextPath = explicitNext || "/welcome";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            toast.error("Password must be at least 8 characters.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/customer/signup", {
                email: email.trim().toLowerCase(), password,
                first_name: firstName.trim(), last_name: lastName.trim(),
            });
            track("tcp_signup_success");
            toast.success("Account created. Please sign in.");
            navigate(`/account/login?next=${encodeURIComponent(nextPath)}`);
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail || "Sign-up failed.";
            if (status === 503) {
                toast.message("Sign-up isn't configured here yet. Subscribe on the storefront to create your account.");
                setTimeout(() => { window.location.href = MEMBERSHIP_URL; }, 1500);
            } else if (status === 409) {
                toast.error("An account with that email already exists. Try signing in.");
            } else {
                toast.error(detail);
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <Frame>
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-1">
                    <div className="h-9 px-2 flex items-center justify-center rounded-md bg-white"><img src={require("../assets/logo-tcp-mark.png")} alt="The Context Project" className="h-6 w-auto select-none" draggable={false} /></div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-semibold">The Context Project</div>
                        <div className="text-[11px] text-[#64748B]">Members archive · join</div>
                    </div>
                </div>
            </div>

            <h1 className="text-3xl font-bold text-white tracking-tight mb-3">Join the investigation.</h1>
            <p className="text-[15px] text-[#94A3B8] leading-relaxed mb-6">
                Create your member account. Choose your plan on the storefront after signing in.
            </p>

            <a
                href={MEMBERSHIP_URL}
                data-testid="account-signup-storefront-cta"
                target="_blank"
                rel="noopener noreferrer"
                className="block mb-6 px-4 py-3 rounded-xl border border-[#56CCF2]/40 bg-[#56CCF2]/5 text-[#56CCF2] text-sm tracking-wide inline-flex items-center gap-2 hover:bg-[#56CCF2]/10"
            >
                See membership plans · Monthly / Yearly / Lifetime
                <ExternalLink className="w-3.5 h-3.5" />
            </a>

            <form onSubmit={submit} className="space-y-4" data-testid="account-signup-form">
                <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                        <span className="text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-2 block">First name</span>
                        <div className="flex items-center gap-3 bg-[#111827] border border-[#243044] rounded-xl px-4 py-3 focus-within:border-[#2F80ED]">
                            <User className="w-4 h-4 text-[#64748B]" />
                            <input data-testid="account-signup-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First" className="bg-transparent border-0 outline-none text-white placeholder-[#64748B] flex-1 text-sm" />
                        </div>
                    </label>
                    <label className="block">
                        <span className="text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-2 block">Last name</span>
                        <div className="flex items-center gap-3 bg-[#111827] border border-[#243044] rounded-xl px-4 py-3 focus-within:border-[#2F80ED]">
                            <User className="w-4 h-4 text-[#64748B]" />
                            <input data-testid="account-signup-last" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last" className="bg-transparent border-0 outline-none text-white placeholder-[#64748B] flex-1 text-sm" />
                        </div>
                    </label>
                </div>
                <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-2 block">Email</span>
                    <div className="flex items-center gap-3 bg-[#111827] border border-[#243044] rounded-xl px-4 py-3 focus-within:border-[#2F80ED]">
                        <Mail className="w-4 h-4 text-[#64748B]" />
                        <input data-testid="account-signup-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="bg-transparent border-0 outline-none text-white placeholder-[#64748B] flex-1 text-sm" />
                    </div>
                </label>
                <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-2 block">Password</span>
                    <div className="flex items-center gap-3 bg-[#111827] border border-[#243044] rounded-xl px-4 py-3 focus-within:border-[#2F80ED]">
                        <Lock className="w-4 h-4 text-[#64748B]" />
                        <input data-testid="account-signup-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" className="bg-transparent border-0 outline-none text-white placeholder-[#64748B] flex-1 text-sm" />
                    </div>
                </label>

                <button data-testid="account-signup-submit" type="submit" disabled={busy} className="w-full mt-2 px-5 py-3.5 rounded-xl bg-[#2F80ED] hover:bg-[#1E6FE0] disabled:opacity-60 text-white text-sm font-semibold tracking-wide inline-flex items-center justify-center gap-2">
                    {busy ? "Creating account…" : "Create account"}
                    <ArrowRight className="w-4 h-4" />
                </button>
            </form>

            <p className="mt-6 text-center text-xs text-[#64748B]">
                Already a member?{" "}
                <Link to="/account/login" data-testid="account-signup-login-link" className="text-[#56CCF2] hover:text-white">Sign in</Link>
            </p>
        </Frame>
    );
}
